<template>
  <div>
    <v-row v-if="!embedded" class="align-center">
      <v-col cols="12" class="d-flex align-center">
        <gg-card-title class="pt-0 mr-auto">Add a Server</gg-card-title>
        <gg-btn tile outlined color="primary" :to="{ name: 'hosting_kvm_list' }">Back to servers</gg-btn>
      </v-col>
    </v-row>

    <gg-card :flat="embedded">
      <gg-card-text class="pa-0">
        <v-stepper v-model="step" flat>
          <v-stepper-header v-if="!embedded">
            <v-stepper-step :complete="step > 1" step="1">Server type</v-stepper-step>
            <v-divider />
            <v-stepper-step :complete="step > 2" step="2">Choose a plan</v-stepper-step>
            <v-divider />
            <v-stepper-step :complete="step > 3" step="3">Configure &amp; pay</v-stepper-step>
            <v-divider />
            <v-stepper-step step="4">Provisioning</v-stepper-step>
          </v-stepper-header>

          <v-stepper-items>
            <!-- ── Step 1: managed or unmanaged ──────────────────────── -->
            <v-stepper-content step="1">
              <div class="subtitle-2 mb-3" id="kvm-order-kind-label">Which kind of server?</div>
              <div role="radiogroup" aria-labelledby="kvm-order-kind-label">
              <v-row dense>
                <v-col v-for="k in kinds" :key="k.value" cols="12" sm="6">
                  <!-- .native: GGCard forwards $attrs but not listeners. -->
                  <gg-card outlined class="kvm-order__kind" :class="{ 'kvm-order__kind--on': kind === k.value }"
                           role="radio" tabindex="0" :aria-checked="kind === k.value ? 'true' : 'false'"
                           @click.native="chooseKind(k.value)" @keydown.native.enter.prevent="chooseKind(k.value)"
                           @keydown.native.space.prevent="chooseKind(k.value)">
                    <gg-card-text class="pa-4">
                      <div class="d-flex align-center mb-1">
                        <v-icon class="mr-2 kvm-order__kind-icon" aria-hidden="true">{{ k.icon }}</v-icon>
                        <span class="body-1 font-weight-medium">{{ k.title }}</span>
                      </div>
                      <div class="kvm-order__muted text-caption">{{ k.text }}</div>
                    </gg-card-text>
                  </gg-card>
                </v-col>
              </v-row>
              </div>
            </v-stepper-content>

            <!-- ── Step 2: choose a plan ───────────────────────────── -->
            <v-stepper-content step="2">
              <div v-if="loadingCatalog" class="py-8 text-center" role="status" aria-live="polite">
                <v-progress-circular indeterminate color="primary" size="36" width="3" aria-hidden="true" />
                <span class="kvm-order__sr-only">Loading plans…</span>
              </div>
              <v-alert v-else-if="catalogError" type="error" border="left" dense role="alert">
                {{ catalogError.message || 'The catalog could not be loaded.' }}
                <div class="mt-2">
                  <gg-btn small tile color="white" class="error--text" @click="loadCatalog()">
                    <v-icon left small aria-hidden="true">mdi-refresh</v-icon>Retry
                  </gg-btn>
                </div>
              </v-alert>

              <template v-else>
                <!-- mt-3: the stepper clips its content (overflow: hidden), and an
                     outlined field's label floats ABOVE the box once it has a value —
                     as the step's first field it needs room or "Plan" is cut off. -->
                <v-select
                  v-model="productName" :items="productNames" label="Plan"
                  outlined dense hide-details class="mt-3 mb-4" style="max-width: 420px;"
                />
                <v-select
                  v-if="locations.length" v-model="location" :items="locationItems"
                  label="Location" outlined dense hide-details class="mb-4" style="max-width: 420px;"
                />
                <!-- Checked a moment after the customer stops typing, and the plans
                     re-price with it: green once core accepts the code, red when it
                     doesn't. The icon and message say the same without the colour. -->
                <v-text-field
                  v-model="coupon" label="Coupon code" outlined dense autocomplete="off"
                  class="mb-2" :class="couponState && `kvm-order__coupon--${couponState}`"
                  style="max-width: 420px;"
                  :loading="couponState === 'checking'" :append-icon="couponIcon" :messages="couponMessage"
                  @input="couponChanged" @keydown.enter.prevent="checkCoupon"
                />

                <template v-if="location">
                  <div role="group" aria-labelledby="kvm-order-cycle-label">
                    <div id="kvm-order-cycle-label" class="subtitle-2 mb-2">Billing cycle</div>
                    <v-radio-group v-model="term" dense hide-details class="mt-0 mb-2" aria-labelledby="kvm-order-cycle-label">
                      <v-radio v-for="p in location.pricing || []" :key="p.term" :value="p.term">
                        <template v-slot:label>
                          <span class="text-capitalize mr-2">{{ p.term }}</span>
                          <strong>{{ money(p.price) }}</strong>
                          <s v-if="p.couponapplied" class="kvm-order__muted ml-1"><span class="kvm-order__sr-only">was </span>{{ money(p.standard_price) }}</s>
                          <span class="kvm-order__muted ml-2">({{ money(p.permonth) }}/mo)</span>
                          <span v-if="p.couponapplied" class="kvm-order__deal ml-2">{{ dealLabel(p) }}</span>
                          <span v-if="Number(p.setupfee) > 0" class="kvm-order__muted ml-2">+ {{ money(p.setupfee) }} setup</span>
                        </template>
                      </v-radio>
                    </v-radio-group>
                  </div>
                </template>

                <div class="mt-4">
                  <gg-btn v-if="!embedded" text class="mr-2" @click="step = 1">Back</gg-btn>
                  <gg-btn tile color="primary" :disabled="!location || !term || couponState === 'checking'" @click="step = 3">Continue</gg-btn>
                </div>
              </template>
            </v-stepper-content>

            <!-- ── Step 3: configure & pay ─────────────────────────── -->
            <v-stepper-content step="3">
              <v-row>
                <v-col cols="12" md="7">
                  <div class="subtitle-2 mb-1">Server label <span class="kvm-order__muted">(optional)</span></div>
                  <v-text-field
                    v-model="label" placeholder="my-server" outlined dense persistent-hint
                    hint="A name for this server. Leave blank for an auto-assigned one."
                    aria-label="Server label" autocomplete="off"
                    class="mb-4" style="max-width: 420px;"
                  />

                  <v-divider class="mb-3" />
                  <div role="group" aria-labelledby="kvm-order-payment-label">
                    <div id="kvm-order-payment-label" class="subtitle-2 mb-2">Payment</div>
                    <v-radio-group v-model="cardMode" dense hide-details class="mt-0 mb-2" aria-labelledby="kvm-order-payment-label">
                      <v-radio v-if="hasCardOnFile" value="onfile">
                        <template v-slot:label>Card on file <span class="kvm-order__muted ml-1">({{ cardOnFileLabel }})</span></template>
                      </v-radio>
                      <v-radio value="new" label="Use a new card" />
                    </v-radio-group>

                    <div v-if="cardMode === 'new'" class="d-flex flex-wrap" style="max-width: 420px;" role="group" aria-label="New card details">
                      <v-text-field v-model="cardnum" label="Card number" outlined dense hide-details="auto"
                                    inputmode="numeric" autocomplete="cc-number"
                                    class="mb-3 mr-3" style="min-width: 220px;" />
                      <v-text-field v-model="expdate" label="Expiry (MM/YY)" placeholder="10/27" outlined dense
                                    hide-details="auto" inputmode="numeric" autocomplete="cc-exp"
                                    class="mb-3" style="max-width: 130px;" />
                    </div>
                    <v-text-field v-model="cardcvv" label="CVV" outlined dense hide-details="auto"
                                  inputmode="numeric" autocomplete="cc-csc"
                                  class="mb-2" style="max-width: 130px;" />
                  </div>

                  <v-alert type="info" dense text class="mt-2 mb-0 kvm-order__alert-soft">
                    Your card is pre-authorized for the first term now; it's charged once the server is provisioned.
                  </v-alert>
                </v-col>

                <!-- Order summary -->
                <v-col cols="12" md="5">
                  <gg-card outlined>
                    <gg-card-text class="pa-4">
                      <h3 class="body-1 mb-3">Order summary</h3>
                      <div v-for="row in summaryRows" :key="row.label" class="d-flex py-1">
                        <span class="kvm-order__muted mr-auto">{{ row.label }}</span>
                        <span :class="{ 'font-weight-medium': row.strong }">{{ row.value }}</span>
                      </div>
                      <v-divider class="my-2" />
                      <div class="d-flex py-1">
                        <span class="mr-auto font-weight-medium">Due today</span>
                        <span class="font-weight-bold">{{ money(selectedPricing && selectedPricing.price) }}</span>
                      </div>
                      <div v-if="renewalPrice" class="kvm-order__muted text-caption">
                        Renews at {{ renewalPrice }}/term.
                      </div>
                    </gg-card-text>
                  </gg-card>
                </v-col>
              </v-row>

              <div class="mt-4">
                <gg-btn text class="mr-2" @click="step = 2">Back</gg-btn>
                <gg-btn tile color="primary" :loading="submitting" :disabled="!canPlaceOrder" @click="placeOrder">
                  Place order
                </gg-btn>
                <div v-if="!canPlaceOrder && !submitting" class="kvm-order__muted text-caption mt-2" aria-live="polite">
                  Enter your payment details to place the order.
                </div>
              </div>
            </v-stepper-content>

            <!-- ── Step 4: provisioning ────────────────────────────── -->
            <v-stepper-content step="4">
              <div aria-live="polite" aria-atomic="false">
                <div v-if="orderStatus === 'processing' || (!orderStatus && submitting)" class="py-8 text-center" role="status">
                  <v-progress-circular indeterminate color="primary" size="44" width="4" class="mb-4" aria-hidden="true" />
                  <div class="body-1">Setting up your server…</div>
                  <div class="kvm-order__muted mt-1">This usually takes a minute or two. The page updates automatically.</div>
                </div>

                <template v-else-if="orderStatus === 'active'">
                  <v-alert type="success" border="left" prominent class="mb-4 kvm-order__alert-prominent">
                    Your server is ready! It's now in your servers list.
                  </v-alert>
                  <gg-btn tile color="primary" :to="{ name: 'hosting_kvm_list' }">View your servers</gg-btn>
                </template>

                <template v-else-if="orderStatus === 'needs_review'">
                  <v-alert type="info" border="left" prominent class="mb-0 kvm-order__alert-prominent">
                    Your order is being reviewed — our team will follow up shortly.
                  </v-alert>
                </template>

                <template v-else-if="orderStatus === 'declined'">
                  <v-alert type="error" border="left" prominent class="mb-4 kvm-order__alert-prominent" role="alert">
                    Your card was declined{{ orderReason ? `: ${orderReason}` : '' }}. Enter a new card to retry —
                    your server isn't created until payment succeeds.
                  </v-alert>
                  <div class="d-flex flex-wrap" style="max-width: 460px;" role="group" aria-label="New card details">
                    <v-text-field v-model="cardnum" label="Card number" outlined dense hide-details="auto"
                                  inputmode="numeric" autocomplete="cc-number"
                                  class="mb-3 mr-3" style="min-width: 220px;" />
                    <v-text-field v-model="expdate" label="Expiry (MM/YY)" placeholder="10/27" outlined dense
                                  hide-details="auto" inputmode="numeric" autocomplete="cc-exp"
                                  class="mb-3" style="max-width: 130px;" />
                  </div>
                  <gg-btn tile color="primary" :loading="retrying" :disabled="!cardnum || !expdate" @click="retryWithNewCard">
                    Update card &amp; retry
                  </gg-btn>
                </template>
              </div>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </gg-card-text>
    </gg-card>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import serviceOrder from '@/api/serviceOrder'
import { SERVICE_TYPES } from '@/consts/service'

const POLL_MS = 4000
// The coupon box is checked this long after the last keystroke.
const COUPON_DELAY_MS = 600

// TEMPORARY promo: every order starts with this coupon applied (core coupon
// `vps50off`: 50% off the first year, annual billing only). If core stops
// accepting it, the page quietly falls back to standard prices. Set to '' to stop.
const AUTO_COUPON = 'vps50off'

const TERM_PERIOD = { monthly: 'month', annually: 'year', biennially: '2 years', triennially: '3 years' }

// The two kinds of server: unmanaged = our KVM servers (Prox, the customer runs
// them); managed = the original VPS product (MVM), managed by our team. Each
// orders from its own catalog group and opens as its own kind of service.
const KINDS = {
  unmanaged: {
    value: 'unmanaged',
    group: 'kvm',
    type: SERVICE_TYPES.KVM,
    route: 'hosting_kvm_info',
    icon: 'mdi-console',
    title: 'Self-Managed VPS',
    text: 'Full root access — you run it. Choose any operating system, or one-click apps with Docker. Ready in minutes.',
  },
  managed: {
    value: 'managed',
    group: 'vps',
    type: SERVICE_TYPES.VPS,
    route: 'hosting_vserver_info',
    icon: 'mdi-face-agent', // (the app's MDI 4.5 has no "account-wrench")
    title: 'Managed VPS with cPanel',
    text: 'Managed by our team — our original VPS plans, with server management included.',
  },
}

// Core formats some coupon prices with a thousands separator ("1,319.88").
const toNumber = v => Number(String(v).replace(/,/g, ''))

// Core's coupon pricing re-adds every cycle the coupon doesn't cover, including
// ones disabled in WHMCS (price -1). Only offer cycles that have a real price.
const offeredOnly = products => products.map(p => ({
  ...p,
  locations: (p.locations || []).map(l => ({
    ...l,
    pricing: (l.pricing || []).filter(t => toNumber(t.price) >= 0),
  })),
}))

/**
 * Add a Server: managed or unmanaged → plan → configure & pay → the order goes
 * through. On its own page it then opens the new server (`?type=managed|unmanaged`
 * skips the first question). `embedded` (inside another flow, e.g. deploying an
 * app on a new server) is always unmanaged, drops the page title and step header
 * and emits `ordered` with the new server's id instead of navigating.
 */
export default {
  name: 'HostingKvmOrder',
  props: {
    embedded: { type: Boolean, default: false },
  },
  data: () => ({
    findAttempts: 0,
    step: 1,
    kind: null, // 'unmanaged' | 'managed' (KINDS)
    loadingCatalog: true,
    catalogError: null,
    products: [],
    catalogSeq: 0, // only the latest catalog fetch may land (the coupon can change mid-flight)
    couponError: null, // why core refused the code in the box
    coupon: AUTO_COUPON,
    pricedCoupon: '', // the code the prices on screen came from ('' = standard prices)
    checkingCoupon: false,
    couponTimer: null,
    // selection
    productName: '',
    location: null, // the chosen location object ({ name, product_id, pricing })
    term: '',
    // config + payment
    label: '',
    cardMode: 'onfile',
    cardnum: '',
    expdate: '',
    cardcvv: '',
    // order
    submitting: false,
    retrying: false,
    orderId: null,
    orderStatus: '',
    orderReason: '',
    pollTimer: null,
    // Service ids (of the kind ordered) that existed before this order, to spot
    // the new one on success.
    existingKvmIds: [],
  }),
  computed: {
    kinds () {
      return Object.values(KINDS)
    },
    kindInfo () {
      return KINDS[this.kind] || KINDS.unmanaged
    },
    ...mapGetters(['SessionProfile', 'services']),
    productNames () {
      return this.products.map(p => p.name)
    },
    selectedProduct () {
      return this.products.find(p => p.name === this.productName) || null
    },
    locations () {
      return (this.selectedProduct && this.selectedProduct.locations) || []
    },
    // The select's v-model gets each item's `value` — the location object itself
    // (NOT return-object, which would hand back this {text, value} wrapper, with no
    // `pricing`, so the billing cycles vanished after picking a location).
    locationItems () {
      return this.locations.map(l => ({ text: l.name, value: l }))
    },
    selectedPricing () {
      return ((this.location && this.location.pricing) || []).find(p => p.term === this.term) || null
    },
    // The coupon is only sent when it discounts the chosen cycle: core rejects the
    // order otherwise (vps50off on monthly), so say why the price didn't drop. It's
    // the code the prices on screen came from, so the order matches what was shown.
    couponApplies () {
      return !!(this.pricedCoupon && this.selectedPricing && this.selectedPricing.couponapplied)
    },
    couponNote () {
      if (!this.pricedCoupon || !this.selectedPricing || this.couponApplies) return null
      return `"${this.pricedCoupon}" doesn't apply to ${this.term} billing.`
    },
    // '' (nothing to say) | checking | valid | invalid — drives the box's colour.
    couponState () {
      if (this.checkingCoupon) return 'checking'
      if (this.couponError) return 'invalid'
      return this.pricedCoupon && this.pricedCoupon === this.coupon.trim() ? 'valid' : ''
    },
    couponIcon () {
      return { valid: 'mdi-check-circle', invalid: 'mdi-alert-circle' }[this.couponState]
    },
    couponMessage () {
      if (this.couponState === 'invalid') return [this.couponError]
      if (this.couponState === 'valid') return [this.couponNote || 'Coupon applied.']
      return []
    },
    renewalPrice () {
      const p = this.selectedPricing
      return p && p.standard_price ? this.money(p.standard_price) : null
    },
    hasCardOnFile () {
      return !!(this.SessionProfile && this.SessionProfile.card_lastfour)
    },
    cardOnFileLabel () {
      const p = this.SessionProfile || {}
      return [p.card_type, p.card_lastfour].filter(Boolean).join(' ••• ')
    },
    canPlaceOrder () {
      if (this.submitting || !this.location || !this.term) return false
      if (!this.cardcvv.trim()) return false
      if (this.cardMode === 'new' && (!this.cardnum.trim() || !this.expdate.trim())) return false
      return true
    },
    summaryRows () {
      if (!this.location) return []
      return [
        { label: 'Plan', value: this.productName, strong: true },
        { label: 'Location', value: this.location.name },
        { label: 'Billing cycle', value: this.term, strong: false },
        ...(this.couponApplies ? [{ label: 'Coupon', value: this.pricedCoupon }] : []),
      ]
    },
  },
  watch: {
    // Reset the location/term when the plan changes; default to the first location.
    productName () {
      this.location = this.locations[0] || null
      this.term = ''
    },
    // A different location → choose its cycle afresh. The SAME location re-read
    // (a coupon re-fetch) keeps the chosen cycle if it's still offered.
    location (now, old) {
      const same = now && old && now.product_id === old.product_id
      if (!same || !(now.pricing || []).some(p => p.term === this.term)) this.term = ''
    },
  },
  created () {
    // Inside another flow (an app on a new Docker host) it's always unmanaged;
    // ?type= answers the first question too.
    const asked = this.embedded ? 'unmanaged' : (this.$route && this.$route.query.type)
    if (KINDS[asked]) this.chooseKind(asked)
    if (!this.hasCardOnFile) this.cardMode = 'new'
  },
  beforeDestroy () {
    this.stopPolling()
    clearTimeout(this.couponTimer)
  },
  methods: {
    ...mapActions(['getServices', 'makecall', 'reportError', 'reportSuccess']),
    money (v) {
      const n = toNumber(v)
      return Number.isFinite(n) ? `$${n.toFixed(2)}` : '—'
    },
    // "50% off first year" for a coupon-discounted cycle.
    dealLabel (p) {
      const saved = toNumber(p.savings)
      const was = toNumber(p.standard_price)
      const off = was > 0 ? `${Math.round(saved / was * 100)}% off` : `Save ${this.money(saved)}`
      return `${off} first ${TERM_PERIOD[p.term] || 'term'}`
    },
    // Managed or unmanaged: its own plans (the promo is for unmanaged servers).
    chooseKind (kind) {
      if (kind !== this.kind) {
        this.kind = kind
        this.products = []
        this.productName = ''
        this.location = null
        this.term = ''
        this.coupon = kind === 'unmanaged' ? AUTO_COUPON : ''
        this.couponError = null
        clearTimeout(this.couponTimer)
        this.loadCatalog()
      }
      this.step = 2
    },
    // `quiet` (a coupon check) re-prices in place instead of blanking the step.
    async loadCatalog ({ quiet = false } = {}) {
      const seq = ++this.catalogSeq
      const coupon = this.coupon.trim()
      try {
        if (quiet) {
          this.checkingCoupon = true
        } else {
          this.loadingCatalog = true
          this.catalogError = null
        }
        const { products, couponError } = await serviceOrder.catalog({ group: this.kindInfo.group, coupon: coupon || undefined })
        if (seq !== this.catalogSeq) return // superseded — the code (or kind) changed meanwhile
        // The promo the page applied itself has lapsed: drop it quietly rather than
        // warn the customer about a code they never typed.
        if (couponError && !quiet && coupon === AUTO_COUPON) {
          this.coupon = ''
          this.couponError = null
        } else {
          this.couponError = couponError
        }
        this.pricedCoupon = couponError ? '' : coupon
        this.products = offeredOnly(products)
        // Restore / default the selection.
        if (!this.productName || !this.products.some(p => p.name === this.productName)) {
          this.productName = this.products.length ? this.products[0].name : ''
        }
        // Point at the location in the FRESH catalog (e.g. coupon prices), matched
        // by product id — never a stale object from the previous fetch.
        const previous = this.location && this.location.product_id
        this.location = this.locations.find(l => l.product_id === previous) || this.locations[0] || null
      } catch (e) {
        if (seq !== this.catalogSeq) return
        // A failed coupon check keeps the plans (and prices) already on screen.
        if (quiet) this.couponError = 'The coupon could not be checked. Please try again.'
        else this.catalogError = e
      } finally {
        if (seq === this.catalogSeq) {
          this.loadingCatalog = false
          this.checkingCoupon = false
        }
      }
    },
    // Typing in the coupon box: check it once the customer pauses.
    couponChanged () {
      clearTimeout(this.couponTimer)
      this.couponError = null
      this.checkingCoupon = true
      this.couponTimer = setTimeout(this.checkCoupon, COUPON_DELAY_MS)
    },
    checkCoupon () {
      clearTimeout(this.couponTimer)
      this.loadCatalog({ quiet: true })
    },
    async placeOrder () {
      if (!this.canPlaceOrder) return
      // Snapshot the current servers of this kind so we can spot the new one once it's active.
      this.existingKvmIds = (this.services || [])
        .filter(s => s.type === this.kindInfo.type).map(s => s.id)
      const payload = {
        product_id: this.location.product_id,
        billing_term: this.term,
        cardcvv: this.cardcvv.trim(),
      }
      if (this.label.trim()) payload.domain = this.label.trim()
      if (this.couponApplies) payload.coupon = this.pricedCoupon
      if (this.cardMode === 'new') {
        payload.cardnum = this.cardnum.replace(/\s|-/g, '')
        payload.expdate = this.expdate.replace(/\D/g, '') // MMYY
      }
      try {
        this.submitting = true
        this.orderId = await serviceOrder.add(payload)
        this.orderStatus = 'processing'
        this.step = 4
        this.startPolling()
      } catch (e) {
        this.reportError(e)
      } finally {
        this.submitting = false
      }
    },
    startPolling () {
      this.stopPolling()
      this.pollTimer = setTimeout(this.poll, POLL_MS)
    },
    stopPolling () {
      if (this.pollTimer) { clearTimeout(this.pollTimer); this.pollTimer = null }
    },
    async poll () {
      try {
        const { status, reason } = await serviceOrder.status(this.orderId)
        this.orderStatus = status
        this.orderReason = reason || ''
        if (status === 'active') {
          // Refresh the list, then jump straight to the new server's manage page.
          await this.getServices({ withDomains: false })
          const created = (this.services || []).find(
            s => s.type === this.kindInfo.type && !this.existingKvmIds.includes(s.id))
          if (created && this.embedded) {
            this.$emit('ordered', String(created.id)) // the page we're part of takes it from here
          } else if (created) {
            this.$router.push({ name: this.kindInfo.route, params: { hostingid: created.id } })
              .catch(() => { /* ignore redundant navigation */ })
          } else if (this.embedded && ++this.findAttempts < 8) {
            // Paid, but not in the services list yet — look again shortly.
            this.pollTimer = setTimeout(this.poll, POLL_MS)
          }
          return // terminal — stop polling (falls back to the success screen if not found)
        }
        if (status === 'declined' || status === 'needs_review') return // terminal
        this.pollTimer = setTimeout(this.poll, POLL_MS) // processing → keep going
      } catch (e) {
        // transient — keep polling
        this.pollTimer = setTimeout(this.poll, POLL_MS)
      }
    },
    async retryWithNewCard () {
      try {
        this.retrying = true
        const res = await this.makecall({
          method: 'creditcard_update',
          data: { slot: 'primary', cardnum: this.cardnum.replace(/\s|-/g, ''), cardexp: this.expdate.replace(/\D/g, '') },
        })
        if (res && res.result && res.result !== 'success') {
          throw new Error(res.resultmsg || 'Could not update the card.')
        }
        await serviceOrder.retry(this.orderId)
        this.orderStatus = 'processing'
        this.orderReason = ''
        this.startPolling()
      } catch (e) {
        this.reportError(typeof e === 'string' ? new Error(e) : e)
      } finally {
        this.retrying = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import './kvm-ui';

// Managed / unmanaged choice cards.
.kvm-order__kind {
  cursor: pointer;
  height: 100%;
  transition: box-shadow 0.15s;
  &:hover { box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1); }
  &:focus-visible { @include kvm-focus-ring; }
  @include kvm-reduced-motion { transition: none; }
}
.kvm-order__kind--on { border-color: $kvm-focus-light !important; box-shadow: inset 0 0 0 1px $kvm-focus-light; }
.kvm-order__kind-icon.v-icon { @include kvm-secondary-text; }

// Contrast-safe replacement for the faint grey secondary text used across the
// wizard (summary labels, optional/helper captions, per-month pricing, renewal,
// provisioning sub-text). Theme-aware — AA in both light and dark.
.kvm-order__muted {
  @include kvm-secondary-text;
}

.kvm-order__sr-only {
  @include kvm-sr-only;
}

.kvm-order__deal {
  @include kvm-text-green-aa;
  font-weight: 500;
}

// Coupon box: green once core accepts the code, red when it refuses it. Light
// tints keep the typed code at AA contrast; the border and icon carry the colour
// (!important over Vuetify's focused-field border, as the box keeps focus).
.kvm-order__coupon--valid ::v-deep .v-input__slot { background-color: #e8f5e9 !important; }
.kvm-order__coupon--invalid ::v-deep .v-input__slot { background-color: #ffebee !important; }
.theme--dark .kvm-order__coupon--valid ::v-deep .v-input__slot { background-color: rgba(61, 177, 78, 0.18) !important; }
.theme--dark .kvm-order__coupon--invalid ::v-deep .v-input__slot { background-color: rgba(239, 83, 80, 0.2) !important; }

.kvm-order__coupon--valid ::v-deep fieldset,
.kvm-order__coupon--valid ::v-deep .v-input__append-inner .v-icon { border-color: $kvm-focus-light !important; color: $kvm-focus-light !important; }
.kvm-order__coupon--invalid ::v-deep fieldset,
.kvm-order__coupon--invalid ::v-deep .v-input__append-inner .v-icon { border-color: #c62828 !important; color: #c62828 !important; }
.theme--dark .kvm-order__coupon--valid ::v-deep fieldset,
.theme--dark .kvm-order__coupon--valid ::v-deep .v-input__append-inner .v-icon { border-color: $kvm-focus-dark !important; color: $kvm-focus-dark !important; }
.theme--dark .kvm-order__coupon--invalid ::v-deep fieldset,
.theme--dark .kvm-order__coupon--invalid ::v-deep .v-input__append-inner .v-icon { border-color: #ef5350 !important; color: #ef5350 !important; }

// --- Alert body text contrast (WCAG 1.4.3) -----------------------------------
// `text`-variant alerts (coupon warning, pre-auth info) render the type colour
// AS the body text on a faint same-hue tint — warning amber ~1.7:1, info blue
// ~3:1 in light theme. Force the message text to an AA-safe near-black (light)
// and a near-white (dark); the coloured icon + border are untouched so meaning
// is preserved. Targets only `.v-alert__content`, never the icon.
.kvm-order__alert-soft ::v-deep .v-alert__content {
  color: #212121 !important; // on faint tint over white ≈ 13:1
}

.theme--dark .kvm-order__alert-soft ::v-deep .v-alert__content {
  color: #eceff1 !important; // on tint over dark surface ≈ 11:1
}

// Prominent (filled) alerts render white text on the solid type colour:
// success #fff-on-#0ca750 = 3.15:1, info ≈ 3.1:1, error ≈ 3.6:1 — all fail.
// Black body text clears AA on the green/blue/red fills in BOTH themes
// (#000 on #0ca750 6.7:1, on #2196f3 6.7:1, on #ff3739 5.9:1, on the lighter
// dark-theme fills higher still); the white badge icon stays for meaning.
.kvm-order__alert-prominent ::v-deep .v-alert__content {
  color: #000 !important;
}

// --- Stepper: legible on mobile + correct in dark mode -----------------------
// Vuetify hides the step labels below ~960px, leaving unlabeled circles. Keep
// the labels visible and let the header wrap so all three steps stay readable
// down to 320px without horizontal clipping.
::v-deep .v-stepper__header {
  flex-wrap: wrap;
  height: auto;
  box-shadow: none;
}

::v-deep .v-stepper__step {
  padding: 16px 12px;
}

// Inactive step labels: lift faint default to AA-contrast secondary text.
::v-deep .v-stepper__label {
  @include kvm-secondary-text;
}

::v-deep .v-stepper__step--active .v-stepper__label,
::v-deep .v-stepper__step--complete .v-stepper__label {
  color: inherit;
}

@media (max-width: $kvm-bp-mobile) {
  ::v-deep .v-stepper__header .v-divider {
    display: none;
  }

  // Override Vuetify's mobile `display: none` so steps keep their names.
  ::v-deep .v-stepper__label {
    display: flex !important;
  }

  ::v-deep .v-stepper__step {
    padding: 12px 10px;
  }
}

// --- Visible focus ring for keyboard users (global default is too weak) -------
::v-deep .v-btn:focus-visible {
  @include kvm-focus-ring;
}

::v-deep .v-radio:focus-within {
  @include kvm-focus-ring;
  border-radius: 8px;
}

// --- Adequate primary target size on mobile (WCAG 2.5.8) ---------------------
@media (max-width: $kvm-bp-mobile) {
  ::v-deep .v-btn:not(.v-size--small):not(.v-size--x-small) {
    min-height: 44px;
  }
}
</style>
